class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.showDrawer = this.showDrawer.bind(this);
    this.hideDrawer = this.hideDrawer.bind(this);

    this.handleAddToCart = this.handleAddToCart.bind(this);

    this.increaseQuantity = this.increaseQuantity.bind(this);
    this.decreaseQuantity = this.decreaseQuantity.bind(this);
    this.manualQuantity = this.manualQuantity.bind(this);

    this.handleProductRemoval = this.handleProductRemoval.bind(this);
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get sectionsToUpdate() {
    return this.dataset.sectionsToUpdate
      ? this.dataset.sectionsToUpdate.split(",").map((s) => `.${s.trim()}`)
      : [];
  }

  connectedCallback() {
    // mini cart
    this.miniCart = this.querySelector(".bosMiniCartContainer");

    // rebind when product grid re-renders
    document.addEventListener("product-grid:updated", () => {
      this.bindEvents();
    });

    this.bindEvents();
  }

  disconnectedCallback() {
    // escape key
    if (!this._escapeBound) {
      this._escapeHandler = (e) => {
        if (e.key === "Escape") this.hideDrawer();
      };
      document.removeEventListener("keydown", this._escapeHandler);
      this._escapeBound = true;
    }

    // menu cart drawer button
    if (this.cartDrawerButton) {
      this.cartDrawerButton.removeEventListener("click", this.showDrawer);
    }

    // overlay
    if (this.drawerOverlay) {
      this.drawerOverlay.removeEventListener("click", this.hideDrawer);
    }

    // mini cart close button
    if (this.closeButton) {
      this.closeButton.removeEventListener("click", this.hideDrawer);
    }

    // add to bag button
    if (this.addToCartButton) {
      this.addToCartButton.forEach((btn) =>
        btn.removeEventListener("click", this.handleAddToCart),
      );
    }

    // quantity changers
    if (this.bosMiniCartProductActionsQuantity) {
      this.bosMiniCartProductActionsQuantity.forEach((wrapper) => {
        const input = wrapper.querySelector(
          ".bosMiniCartProductActionsQuantityInput",
        );
        if (!input) return;
        // manual input
        input.removeEventListener("change", () =>
          this.manualQuantity(wrapper, input),
        );

        // decrease button
        const minus = wrapper.querySelector(
          ".bosMiniCartProductActionsQuantityDecrease",
        );
        if (minus) {
          minus.removeEventListener("click", () =>
            this.decreaseQuantity(wrapper, input),
          );
        }
        // increase button
        const plus = wrapper.querySelector(
          ".bosMiniCartProductActionsQuantityIncrease",
        );
        if (plus) {
          plus.removeEventListener("click", () =>
            this.increaseQuantity(wrapper, input),
          );
        }
      });
    }

    // remove button
    if (this.bosMiniCartProductActionsRemove) {
      this.bosMiniCartProductActionsRemove.forEach((btn) =>
        btn.removeEventListener("click", this.handleProductRemoval),
      );
    }
  }

  bindEvents() {
    // escape key
    if (!this._escapeBound) {
      this._escapeHandler = (e) => {
        if (e.key === "Escape") this.hideDrawer();
      };

      document.addEventListener("keydown", this._escapeHandler);
      this._escapeBound = true;
    }

    // menu cart drawer button
    this.cartDrawerButton = document.querySelector(".bosHeaderCartBtn");
    if (this.cartDrawerButton) {
      this.cartDrawerButton.addEventListener("click", this.showDrawer);
    }

    // overlay
    this.drawerOverlay = this.querySelector(".bosMiniCartOverlay");
    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener("click", this.hideDrawer);
    }

    // mini cart close button
    this.closeButton = this.querySelector(
      ".bosMiniCartContainerHeaderDetailsClose",
    );
    if (this.closeButton) {
      this.closeButton.addEventListener("click", this.hideDrawer);
    }

    // add to bag button
    this.addToCartButton = document.querySelectorAll(".bosAddToCart");
    if (this.addToCartButton) {
      this.addToCartButton.forEach((btn) =>
        btn.addEventListener("click", this.handleAddToCart),
      );
    }

    // quantity changers
    this.bosMiniCartProductActionsQuantity = this.querySelectorAll(
      ".bosMiniCartProductActionsQuantity",
    );
    if (this.bosMiniCartProductActionsQuantity.length > 0) {
      this.bosMiniCartProductActionsQuantity.forEach((wrapper) => {
        const input = wrapper.querySelector(
          ".bosMiniCartProductActionsQuantityInput",
        );
        if (!input) return;

        // manual input
        input.addEventListener("change", () =>
          this.manualQuantity(wrapper, input),
        );

        // decrease button
        const minus = wrapper.querySelector(
          ".bosMiniCartProductActionsQuantityDecrease",
        );
        if (minus) {
          minus.addEventListener("click", () =>
            this.decreaseQuantity(wrapper, input),
          );
        }
        // increase button
        const plus = wrapper.querySelector(
          ".bosMiniCartProductActionsQuantityIncrease",
        );
        if (plus) {
          plus.addEventListener("click", () =>
            this.increaseQuantity(wrapper, input),
          );
        }
      });
    }

    // remove button
    this.bosMiniCartProductActionsRemove = this.querySelectorAll(
      ".bosMiniCartProductActionsRemove",
    );
    if (this.bosMiniCartProductActionsRemove.length > 0) {
      this.bosMiniCartProductActionsRemove.forEach((btn) =>
        btn.addEventListener("click", this.handleProductRemoval),
      );
    }
  }

  // Every cask row in the mini cart carries data-storage-variant-id
  // (rendered from the cask product's storage metafield in Liquid).
  // Storage line items themselves don't have this attribute, so they're
  // naturally excluded from the sum. This scans whatever is currently in
  // the DOM (after any local input.value change / row removal has
  // already happened) and returns { storageVariantId: totalCaskQty }.
  get caskStorageVariantId() {
    const id = parseInt(this.dataset.caskStorageVariantId);
    return id || null;
  }

  calculateStorageTotals() {
    const totals = {};
    if (this.caskStorageVariantId) {
      totals[this.caskStorageVariantId] = 0;
    }
    this.querySelectorAll(
      ".bosMiniCartProductActionsQuantity[data-storage-variant-id]",
    ).forEach((wrapper) => {
      const storageVariantId = parseInt(wrapper.dataset.storageVariantId);
      if (!storageVariantId) return;
      const input = wrapper.querySelector(
        ".bosMiniCartProductActionsQuantityInput",
      );
      const qty = input ? parseInt(input.value) || 0 : 0;
      totals[storageVariantId] = (totals[storageVariantId] || 0) + qty;
    });
    return totals;
  }

  decreaseQuantity(wrapper, input) {
    const newQty = parseInt(input.value) - 1;
    const productId = parseInt(input.dataset.productId);

    if (newQty <= 0) {
      wrapper.closest(".bosMiniCartProduct")?.remove();
      this.handleCartUpdates({
        [productId]: 0,
        ...this.calculateStorageTotals(),
      });
    } else {
      input.value = newQty;
      this.handleCartUpdates({
        [productId]: newQty,
        ...this.calculateStorageTotals(),
      });
    }
  }

  increaseQuantity(wrapper, input) {
    input.value = parseInt(input.value) + 1;
    const productId = parseInt(input.dataset.productId);
    this.handleCartUpdates({
      [productId]: parseInt(input.value),
      ...this.calculateStorageTotals(),
    });
  }

  manualQuantity(wrapper, input) {
    const newQty = parseInt(input.value);
    const productId = parseInt(input.dataset.productId);

    if (isNaN(newQty) || newQty <= 0) {
      wrapper.closest(".bosMiniCartProduct")?.remove();
      this.handleCartUpdates({
        [productId]: 0,
        ...this.calculateStorageTotals(),
      });
    } else {
      input.value = newQty;
      this.handleCartUpdates({
        [productId]: newQty,
        ...this.calculateStorageTotals(),
      });
    }
  }

  handleProductRemoval(e) {
    const target = e.currentTarget;
    if (!target) return;
    const productId = parseInt(target.dataset.productId);
    if (!productId) return;
    target.closest(".bosMiniCartProduct")?.remove();
    this.handleCartUpdates({
      [productId]: 0,
      ...this.calculateStorageTotals(),
    });
  }

  handleAddToCart(e) {
    const target = e.currentTarget;

    if (!target) return;
    const productId = target.dataset.productId;
    const productQuantity = target.dataset.productQuantity;

    if (!productId && !productQuantity) return;

    const item = {
      id: productId,
      quantity: Number(productQuantity),
    };

    let additionalItem = null;

    const productSellingPlan = document.querySelector(".productSellingPlan");
    if (productSellingPlan) {
      const caskStorageVariantId =
        productSellingPlan.dataset.caskStorageVariantId;
      const sellingPlanId = productSellingPlan.dataset.sellingPlanId;

      additionalItem = {
        id: parseInt(caskStorageVariantId),
        // match the cask's own quantity rather than hardcoding 1, so a
        // multi-quantity add starts the storage total off correctly too
        quantity: Number(productQuantity),
        selling_plan: parseInt(sellingPlanId),
      };
    }

    // Bottling
    const bottleStorageFee = target.dataset.bottleStorageFee;
    const orderNote = target.dataset.orderNote;
    const customerName = target.dataset.customerName;

    if (orderNote) {
      const orderDate = target.dataset.orderDate;
      item.properties = {
        "Bottle Status": orderNote,
        // ...(bottleStorageFee && { "Bottle Storage Fee": bottleStorageFee }),
        ...(orderDate && { "Bottle Date": orderDate }),
        ...(customerName && { "Customer Name": customerName }),
        _uid: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      };
    }

    const formData = {
      items: additionalItem ? [item, additionalItem] : [item],
    };

    // build url
    const url = new URL(
      window.location.origin + window.Shopify.routes.root + "cart/add.js",
    );

    // get sections
    const sections = this.sectionId;
    if (!sections) return;
    url.searchParams.set("sections", sections);

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((res) => res.json())
      // .then((data) => {
      //   return this.syncDobCartAttribute().then(() => data);
      // })
      .then((data) => {
        // redirect to config for "Bottle Now"
        if (target.hasAttribute("data-bottle-now") && target.dataset.bottleNowConfiguration === "true") {
          const redirect = target.dataset.bottleNowRedirect;
          if (redirect) {
            window.location.href = redirect;
          } else {
            console.warn(
              "bottle-now button is missing data-bottle-now-redirect",
            );
          }
        } else {
          this.showDrawer();
        }

        // this.showDrawer();
        if (data?.sections && this.sectionId && this.sectionsToUpdate) {
          this.renderSection(
            data.sections[this.sectionId],
            this.sectionsToUpdate,
          );
          this.bindEvents();

          const storageTotals = this.calculateStorageTotals();
          if (Object.keys(storageTotals).length > 0) {
            this.handleCartUpdates(storageTotals);
          }
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  renderSection(html, selectors) {
    const temp = document.createElement("div");
    temp.innerHTML = html;

    selectors.forEach((selector) => {
      const target = document.querySelector(selector);
      const source = temp.querySelector(selector);
      if (!target || !source) {
        console.warn("renderSection: selector not found", selector);
        return;
      }
      target.innerHTML = source.innerHTML;
    });
  }

  getCookie(name) {
    const match = document.cookie.match("(?:^|; )" + name + "=([^;]*)");
    return match ? decodeURIComponent(match[1]) : null;
  }

  getCart() {
    return fetch(window.Shopify.routes.root + "cart.js").then((res) =>
      res.json(),
    );
  }

  syncDobCartAttribute() {
    const dob = this.getCookie("bos_user_dob");

    if (!dob) return Promise.resolve();

    return fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        attributes: {
          DOB: dob,
        },
      }),
    }).then((res) => res.json());
  }

  // Accepts a full { variantId: quantity, ... } map — the cask's own
  // change plus the freshly recalculated storage totals — and sends it
  // as a single cart/update.js call.
  handleCartUpdates(updates) {
    if (!updates || Object.keys(updates).length === 0) return;

    // build url
    const url = new URL(
      window.location.origin + window.Shopify.routes.root + "cart/update.js",
    );

    // get sections
    const sections = this.sectionId;
    if (!sections) return;
    url.searchParams.set("sections", sections);

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ updates }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.sections && this.sectionId && this.sectionsToUpdate) {
          this.renderSection(
            data.sections[this.sectionId],
            this.sectionsToUpdate,
          );
          this.bindEvents();
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  showDrawer() {
    this.disableScrollingBody();
    if (this.drawerOverlay) this.drawerOverlay.classList.remove("hidden");
    if (this.miniCart) this.miniCart.classList.add("isOpen");
    this.miniCart?.classList.add("isActiveDrawer");
  }

  hideDrawer() {
    this.enableScrollingBody();
    if (this.drawerOverlay) this.drawerOverlay.classList.add("hidden");
    if (this.miniCart) this.miniCart.classList.remove("isOpen");
    this.miniCart?.classList.remove("isActiveDrawer");
  }

  disableScrollingBody() {
    document.body.classList.add("disableScrolling");
  }

  enableScrollingBody() {
    document.body.classList.remove("disableScrolling");
  }
}

if (!customElements.get("mini-cart")) {
  customElements.define("mini-cart", MiniCart);
}